import React from 'react';
import PropTypes from 'prop-types';
import Link from '@components/shared/Link';
import { useAppContext } from '@/context/AppContext';
import { iconBoxStyle } from '@/theme/icons';
import { buildLogoStyle, logoFilterStyle } from '@/theme/logo';
import { SiteLogoMark } from '@components/shared/FilteredMark';
import { chromeCornerClass } from '@/slideshow/chrome';

/**
 * Logo for the image viewer (media) or slideshow chrome.
 * Uses brand.logo.mediaFileId / slideshowFileId when set, otherwise the page logo.
 * A slideshow can put it in another corner; the theme's own placement of the
 * viewer logo only applies in the default top-left corner.
 */
const EditorialLogo = ({ siteTitle, closeUrl, onClose, context = 'media', corner = 'tl' }) => {
    const { theme } = useAppContext();
    if (corner === 'hidden') return null;
    const logo = theme?.brand?.logo;
    const moved = corner === 'tl' ? iconBoxStyle(theme?.header?.modules?.['viewer-logo']?.desktop) : undefined;
    const textMode = logo?.mode === 'text';
    const logoStyle = textMode
        ? { ...buildLogoStyle(logo, context), ...logoFilterStyle(logo?.filter) }
        : null;
    const label = textMode ? (logo.text || siteTitle) : siteTitle;
    return (
        <span
            data-header-module="viewer-logo"
            data-slideshow-logo=""
            data-lock-ratio="1"
            className={`absolute ${chromeCornerClass('logo', corner)} z-30 inline-block`}
            style={{
                height: '3.36em',
                width: '160px',
                maxWidth: '100%',
                ...moved,
            }}
        >
            <Link
                aria-label={textMode ? undefined : label}
                className="relative block h-full w-full opacity-100 transition-opacity duration-200 hover:opacity-80"
                style={logoStyle || undefined}
                to={closeUrl}
                onClick={(e) => {
                    e.preventDefault();
                    onClose(closeUrl);
                }}
            >
                {textMode ? label : (
                    <SiteLogoMark
                        logo={logo}
                        context={context}
                        className="pointer-events-none absolute inset-0"
                    />
                )}
            </Link>
        </span>
    );
};

EditorialLogo.propTypes = {
    siteTitle: PropTypes.string.isRequired,
    closeUrl: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    context: PropTypes.oneOf(['media', 'slideshow']),
    corner: PropTypes.oneOf(['tl', 'tr', 'bl', 'br', 'hidden']),
};

export default EditorialLogo;
